import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

/**
 * v5.1 — pill-only. The rectangular button retired. For icon-only
 * buttons use <IconButton /> (40px circle).
 *
 * Variants: primary | secondary | outline | destructive
 * Sizes:    sm (32px) | md (44px, default) | lg (52px)
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-all duration-200 ease-ac-out focus-visible:outline-none focus-visible:ring-0 focus-visible:[box-shadow:var(--ac-glow-mint-sm)] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-[hsl(var(--ac-mint-primary))] text-[hsl(var(--ac-text-dark))] border border-transparent hover:bg-[hsl(var(--ac-mint-secondary))] hover:-translate-y-px hover:[box-shadow:var(--ac-glow-mint-md)]",
        secondary:
          "bg-[hsl(var(--ac-dark-accent)/0.6)] text-[hsl(var(--ac-text-light))] border border-transparent hover:bg-[hsl(var(--ac-dark-accent))] hover:-translate-y-px",
        outline:
          "bg-transparent text-[hsl(var(--ac-mint-primary))] border border-[hsl(var(--ac-mint-primary))] hover:bg-[hsl(var(--ac-mint-primary)/0.12)]",
        destructive:
          "bg-[hsl(var(--ac-danger))] text-[hsl(var(--ac-text-light))] border border-transparent hover:bg-[hsl(var(--ac-danger)/0.85)] hover:-translate-y-px hover:[box-shadow:0_0_24px_hsl(var(--ac-danger)/0.35)]",
      },
      size: {
        sm: "h-8 px-3.5 text-xs",
        md: "h-11 px-5 text-sm",
        lg: "h-[52px] px-6 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  icon?: React.ReactNode
  trailing?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, icon, trailing, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    // Slot requires exactly one child element; only wrap when we aren't delegating.
    const inner = (icon || trailing) && !asChild
      ? <>{icon}{children}{trailing}</>
      : children

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {inner}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
